import {
  isOnboardingIngestChoice,
  type T_OnboardingIngestChoice,
  type T_OnboardingV2SourceSelection,
} from "@lattice/core-lib/types/onboarding/onboarding.types";

import {
  E_OnboardingV2State,
  type T_OnboardingV2State,
} from "./onboarding-v2-machine";

export const ONBOARDING_V2_TYPE = "conversational_v2";
export const ONBOARDING_V2_FUNNEL = "onboarding_v2";
export const ONBOARDING_V2_ROUTE_SOURCE = "onboarding_v2";
export const ONBOARDING_V2_MANUAL_HOLDINGS_SOURCE = "onboarding_v2_manual";

export const E_HoldingsImportAnalyticsEvent = {
  HOLDINGS_IMPORT_STARTED: "Holdings Import Started",
  HOLDINGS_IMPORT_PARSED: "Holdings Import Parsed",
  HOLDINGS_IMPORT_APPLIED: "Holdings Import Applied",
  HOLDINGS_IMPORT_FAILED: "Holdings Import Failed",
  HOLDINGS_IMPORT_CANCELLED: "Holdings Import Cancelled",
  HOLDINGS_IMPORT_RESUMED: "Holdings Import Resumed",
} as const;

export const E_OnboardingAnalyticsEvent = {
  ONBOARDING_STARTED: "Onboarding Started",
  ONBOARDING_STEP_VIEWED: "Onboarding Step Viewed",
  ONBOARDING_STEP_COMPLETED: "Onboarding Step Completed",
  ONBOARDING_STEP_EXITED: "Onboarding Step Exited",
  ONBOARDING_INTERACTION: "Onboarding Interaction",
  ONBOARDING_HANDOFF_STARTED: "Onboarding Handoff Started",
  ONBOARDING_HANDOFF_OUTCOME: "Onboarding Handoff Outcome",
  ONBOARDING_APP_BACKGROUNDED: "Onboarding App Backgrounded",
  ONBOARDING_APP_RESUMED: "Onboarding App Resumed",
  ONBOARDING_EXITED: "Onboarding Exited",
  ONBOARDING_PROGRESS_SAVE_FAILED: "Onboarding Progress Save Failed",
  ONBOARDING_COMPLETED: "Onboarding Completed",
} as const;

export type T_OnboardingAnalyticsProperty =
  | boolean
  | null
  | number
  | string
  | string[];
export type T_OnboardingAnalyticsProperties = Record<
  string,
  T_OnboardingAnalyticsProperty | undefined
>;

export const E_OnboardingV2EntrySource = {
  Signup: "signup",
  LaunchGate: "launch_gate",
  InApp: "in_app",
} as const;

export type T_OnboardingV2EntrySource =
  (typeof E_OnboardingV2EntrySource)[keyof typeof E_OnboardingV2EntrySource];

export const E_OnboardingV2Step = {
  HeardAboutUs: "heard_about_us",
  HoldingsIngestChoice: "holdings_ingest_choice",
  HoldingsHandoff: "holdings_handoff",
  Completion: "completion",
} as const;

export type T_OnboardingV2Step =
  (typeof E_OnboardingV2Step)[keyof typeof E_OnboardingV2Step];

export type T_OnboardingV2AnalyticsContext = {
  attemptId: string;
  attemptStartedAtMs: number;
  entryDetail?: string;
  entrySource: T_OnboardingV2EntrySource;
};

export type T_OnboardingV2RouteParams = {
  attempt_started_at_ms?: string;
  auto_import?: "file" | "photo";
  entry_detail?: string;
  entry_source?: string;
  handoff_started_at_ms?: string;
  ingest_choice?: string;
  onboarding_attempt_id?: string;
  source?: string;
};

const eventSequenceByAttemptId = new Map<string, number>();

const ONBOARDING_V2_STEP_BY_STATE: Record<
  T_OnboardingV2State,
  T_OnboardingV2Step
> = {
  [E_OnboardingV2State.Welcome]: E_OnboardingV2Step.HeardAboutUs,
  [E_OnboardingV2State.Greeting]: E_OnboardingV2Step.HeardAboutUs,
  [E_OnboardingV2State.Prompt]: E_OnboardingV2Step.HeardAboutUs,
  [E_OnboardingV2State.Source]: E_OnboardingV2Step.HeardAboutUs,
  [E_OnboardingV2State.SourceOther]: E_OnboardingV2Step.HeardAboutUs,
  [E_OnboardingV2State.SavingSource]: E_OnboardingV2Step.HeardAboutUs,
  [E_OnboardingV2State.SourceSaveFailed]: E_OnboardingV2Step.HeardAboutUs,
  [E_OnboardingV2State.Typing]: E_OnboardingV2Step.HoldingsIngestChoice,
  [E_OnboardingV2State.DetailPrompt]: E_OnboardingV2Step.HoldingsIngestChoice,
  [E_OnboardingV2State.Detail]: E_OnboardingV2Step.HoldingsIngestChoice,
  [E_OnboardingV2State.SavingDetail]: E_OnboardingV2Step.HoldingsIngestChoice,
  [E_OnboardingV2State.DetailSaveFailed]:
    E_OnboardingV2Step.HoldingsIngestChoice,
  [E_OnboardingV2State.Handoff]: E_OnboardingV2Step.HoldingsHandoff,
};

export function getOnboardingV2StepForState(
  state: unknown,
): T_OnboardingV2Step | null {
  if (!isOnboardingV2State(state)) return null;
  return ONBOARDING_V2_STEP_BY_STATE[state];
}

export function getOnboardingV2ConversationStep(
  state: unknown,
): T_OnboardingV2Step | null {
  return getOnboardingV2StepForState(state);
}

function isOnboardingV2State(state: unknown): state is T_OnboardingV2State {
  switch (state) {
    case E_OnboardingV2State.Welcome:
    case E_OnboardingV2State.Greeting:
    case E_OnboardingV2State.Prompt:
    case E_OnboardingV2State.Source:
    case E_OnboardingV2State.SourceOther:
    case E_OnboardingV2State.SavingSource:
    case E_OnboardingV2State.SourceSaveFailed:
    case E_OnboardingV2State.Typing:
    case E_OnboardingV2State.DetailPrompt:
    case E_OnboardingV2State.Detail:
    case E_OnboardingV2State.SavingDetail:
    case E_OnboardingV2State.DetailSaveFailed:
    case E_OnboardingV2State.Handoff:
      return true;
    default:
      return false;
  }
}

export function getOnboardingV2EntrySource(
  entrySource: string | undefined,
  inAppSource: string | undefined,
): T_OnboardingV2EntrySource {
  if (inAppSource && inAppSource.trim().length > 0) {
    return E_OnboardingV2EntrySource.InApp;
  }
  switch (entrySource) {
    case E_OnboardingV2EntrySource.Signup:
      return E_OnboardingV2EntrySource.Signup;
    case E_OnboardingV2EntrySource.InApp:
      return E_OnboardingV2EntrySource.InApp;
    case E_OnboardingV2EntrySource.LaunchGate:
    default:
      return E_OnboardingV2EntrySource.LaunchGate;
  }
}

export function getOnboardingV2SourceCompletionProperties(
  source: T_OnboardingV2SourceSelection,
): T_OnboardingAnalyticsProperties {
  return {
    input_method: source.value === "OTHER" ? "free_text" : "selection",
    value: source.value,
  };
}

export function getOnboardingV2AnalyticsProperties(
  context: T_OnboardingV2AnalyticsContext,
  extra?: T_OnboardingAnalyticsProperties,
): T_OnboardingAnalyticsProperties {
  return {
    ...extra,
    entry_detail: context.entryDetail,
    entry_source: context.entrySource,
    feature_funnel: ONBOARDING_V2_FUNNEL,
    onboarding_attempt_id: context.attemptId,
    onboarding_type: ONBOARDING_V2_TYPE,
  };
}

export function getNextOnboardingV2EventSequence(attemptId: string): number {
  const nextSequence = (eventSequenceByAttemptId.get(attemptId) ?? 0) + 1;
  eventSequenceByAttemptId.set(attemptId, nextSequence);
  return nextSequence;
}

export function getOnboardingV2RouteAnalyticsContext(
  params: T_OnboardingV2RouteParams,
): T_OnboardingV2AnalyticsContext | null {
  if (
    params.source !== ONBOARDING_V2_ROUTE_SOURCE &&
    params.source !== ONBOARDING_V2_MANUAL_HOLDINGS_SOURCE
  ) {
    return null;
  }

  const attemptId = params.onboarding_attempt_id?.trim();
  const attemptStartedAtMs = Number(params.attempt_started_at_ms);
  if (
    !attemptId ||
    !Number.isFinite(attemptStartedAtMs) ||
    attemptStartedAtMs <= 0
  ) {
    return null;
  }

  return {
    attemptId,
    attemptStartedAtMs,
    entryDetail: params.entry_detail?.trim() || undefined,
    entrySource: getOnboardingV2EntrySource(params.entry_source, undefined),
  };
}

export function getOnboardingV2IngestChoice(
  value: string | undefined,
): T_OnboardingIngestChoice | null {
  return value && isOnboardingIngestChoice(value) ? value : null;
}

export function getOnboardingV2RouteElapsedMs(
  startedAtMs: string | undefined,
  nowMs: number,
): number | undefined {
  const parsed = Number(startedAtMs);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.max(0, nowMs - parsed);
}

/** Accumulates foreground-only time for the currently visible funnel step. */
export class C_OnboardingV2ActiveDuration {
  private activeSinceMs: number | null = null;
  private accumulatedMs = 0;

  pause(nowMs: number): void {
    if (this.activeSinceMs === null) return;
    this.accumulatedMs += Math.max(0, nowMs - this.activeSinceMs);
    this.activeSinceMs = null;
  }

  resume(nowMs: number): void {
    if (this.activeSinceMs !== null) return;
    this.activeSinceMs = nowMs;
  }

  reset(nowMs: number, active: boolean): void {
    this.accumulatedMs = 0;
    this.activeSinceMs = active ? nowMs : null;
  }

  read(nowMs: number): number {
    if (this.activeSinceMs === null) return this.accumulatedMs;
    return this.accumulatedMs + Math.max(0, nowMs - this.activeSinceMs);
  }
}
